import styles from '@/styles/ChatWindow.module.scss';
import type { Dispatch, SetStateAction, FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import {
  getDocumentLinks,
  getInquiryHistory,
} from '@/services/api/chatService';
import { Message } from '@/types/chat/chat';
import { ChatBubble } from './ChatBubble';
import { Modal, Spin } from 'antd';
import { NewChat } from './NewChat';
import PDFViewer from '@/components/pdf/PDFViewer';
import useMomentWithTimezone from '@/hooks/timezone';
import { parseMessage, DocumentLink } from '@/utils/messageParser';
import { useTranslation } from 'react-i18next';

interface DisplayMessagesProps {
  selectedInquiryId: string;
  messages: Message[];
  setMessages: Dispatch<SetStateAction<Message[]>>;
  documentLinks: DocumentLink[];
  setDocumentLinks: Dispatch<SetStateAction<DocumentLink[]>>;
}

const DisplayMessages: FC<DisplayMessagesProps> = ({
  selectedInquiryId,
  messages,
  setMessages,
  documentLinks,
  setDocumentLinks,
}) => {
  const { t } = useTranslation();
  const momentWithTimezone = useMomentWithTimezone();
  const containerRef = useRef<HTMLDivElement>(null);
  const [highlightData, setHighlightData] = useState<any>();
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [cursor, setCursor] = useState<string | null>(null);
  const [convertedMsgs, setConvertedMsgs] = useState<{ [id: string]: string }>(
    {},
  );
  const [isLoadingParse, setIsLoadingParse] = useState(false);
  const size = 10;

  const fetchMessages = async (reset = false) => {
    if (!containerRef.current || isLoading) return;
    if (!hasMore && !reset) return;
    setIsLoading(true);
    const response = await getInquiryHistory(selectedInquiryId, {
      before: reset ? null : cursor,
      size,
    });
    if (!response.success) {
      setIsLoading(false);
      return;
    }
    if (!response.data.length || response.data.length < size) {
      setHasMore(false);
    }
    setMessages((prev: Message[]) => {
      const existingErrors = new Map(
        prev.map((msg: any) => [msg.id, msg.referenceError]),
      );
      const newMessages = response.data.map((msg: any) => ({
        ...msg,
        referenceError: existingErrors.get(msg.id) || msg.referenceError,
      }));
      return reset ? newMessages : [...prev, ...newMessages];
    });
    loadLinks(response.data);
    setCursor(response.next_cursor);
    setIsLoading(false);
  };

  useEffect(() => {
    if (selectedInquiryId) {
      setMessages([]);
      setCursor(null);
      setHasMore(true);
      fetchMessages(true);
    }
  }, [selectedInquiryId]);

  useEffect(() => {
    async function processMessages() {
      const results: { [id: string]: string } = {};
      setIsLoadingParse(true);
      for (const msg of messages) {
        if (!msg.response) continue;

        try {
          const parsed = await parseMessage(msg, documentLinks || []);
          results[msg.id] = parsed;
        } catch (e) {
          results[msg.id] = msg.response;
        }
      }

      setIsLoadingParse(false);
      setConvertedMsgs(results);
    }

    if (messages.length > 0) {
      processMessages();
    }
  }, [messages, documentLinks]);

  let scrollTimeout: NodeJS.Timeout;
  const handleScroll = () => {
    if (scrollTimeout) clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(() => {
      if (!containerRef.current || isLoading || !hasMore) return;
      if (
        containerRef.current.scrollTop <=
        50 +
          containerRef.current.clientHeight -
          containerRef.current.scrollHeight
      ) {
        fetchMessages();
      }
    }, 200);
  };

  const loadLinks = async (listMessages: Message[]) => {
    if (!listMessages.length) return;

    const extractDocumentIds = (documentIds: any): string[] => {
      if (Array.isArray(documentIds)) {
        return documentIds.filter(Boolean);
      }
      if (documentIds && typeof documentIds === 'object') {
        return Object.values(documentIds).filter(Boolean) as string[];
      }
      return [];
    };

    const uniqueDocumentIds = [
      ...new Set(
        listMessages.flatMap(item => extractDocumentIds(item.document_ids)),
      ),
    ];

    if (uniqueDocumentIds.length === 0) return;

    const formData = new FormData();
    uniqueDocumentIds.forEach((id: any) => {
      formData.append('document_ids', id);
    });
    const response = await getDocumentLinks(selectedInquiryId, formData);
    if (!response.success) {
      return;
    }

    setDocumentLinks((prev: DocumentLink[]) => {
      const existingIds = new Set(prev.map((item: DocumentLink) => item.id));
      const newLinks = response.data.filter(
        (item: DocumentLink) => !existingIds.has(item.id),
      );
      return [...prev, ...newLinks];
    });
  };

  return (
    <div ref={containerRef} className={styles.messages} onScroll={handleScroll}>
      {messages && messages.length == 0 && !isLoading && <NewChat />}
      {selectedInquiryId &&
        messages.map((msg: any) => (
          <div key={msg.id}>
            <ChatBubble
              username={msg.sender_id}
              timestamp={msg.created_at}
              message={msg.message}
              avatar={msg.avatar}
              isSender={true}
              loading={msg.waitingForReferences || false}
              isGeneratedReference={msg.is_reference_generated}
              referenceError={msg.referenceError}
            />
            <span
              style={{
                display: 'flex',
                width: '100%',
                justifyContent: 'end',
                marginTop: '-10px',
              }}
            >
              {msg.created_at}
            </span>
            <ChatBubble
              username={t('home.chat.botName')}
              timestamp={msg.updated_at}
              message={
                isLoadingParse
                  ? msg.response
                  : convertedMsgs[msg.id] || msg.response
              }
              avatar={''}
              isSender={false}
              loading={!msg?.response}
              status={msg.status}
              mode={msg.mode}
              setHighlightData={setHighlightData}
              isGeneratedReference={msg.is_reference_generated}
              referenceError={msg.referenceError}
            />

            {msg?.response && (
              <span
                style={{
                  display: 'flex',
                  width: '50%',
                  justifyContent: 'start',
                  marginTop: '-20px',
                  marginLeft: '65px',
                }}
              >
                {msg.updated_at}
              </span>
            )}
          </div>
        ))}
      {isLoading && (
        <div
          style={{
            display: 'flex',
            width: '100%',
            justifyContent: 'center',
            padding: '5px 0',
          }}
        >
          <Spin />
        </div>
      )}
      <Modal
        footer={null}
        destroyOnClose
        open={!!highlightData}
        onCancel={() => setHighlightData(null)}
        width="100vw"
        getContainer={() => document.body}
        style={{
          top: 0,
          padding: 0,
          height: '100vh',
          maxWidth: '100vw',
        }}
      >
        <PDFViewer highlightData={highlightData} />
      </Modal>
    </div>
  );
};

export default DisplayMessages;
